import React, { useEffect, useState } from "react";
import dayjs from "dayjs";
import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Divider,
  Grid,
  Modal,
  Row,
  Select,
  Space,
  Tag,
  Typography,
} from "antd";
import { CheckCircleFilled, ExclamationCircleFilled } from "@ant-design/icons";
import { ChannelEnum, ModeEnum } from "../../constants/enums";
import { ChannelKeyMap } from "../../constants/labels";
import { campaignService } from "../../services/campaignService";
import { billingService, CreditCardInfo } from "../../services/billingService";
import {
  applyPastTimeToday,
  BlockRange,
  buildDisabledTime,
  parseBlockedRanges,
} from "../../utils/date";
import { useBrandStore } from "../../store/brandStore";
import { DateRange } from "../../models/time";

const { Text } = Typography;

type BillingAccount = {
  email: string;
  name: string;
  hasCreditCard: boolean;
  cardBrand?: string;
  cardLast4?: string;
};

type PaymentOption = "account" | "later" | null;

export interface BillingModalProps {
  open: boolean;
  campaignId?: number;
  campaignRunLogId?: number;
  campaignName: string;
  channel: ChannelEnum | null;
  totalFee?: number;
  maxBudget?: number;
  estimatedMembers?: number;
  costPerMessage?: number;
  smsSegments?: number;
  campaignStart?: string;
  campaignEnd?: string;
  mode?: ModeEnum;
  onClose: () => void;
  onPaymentLater: () => void | Promise<void>;
  onSuccess: () => void;
  onFailed: () => void;
  onConfirmPayment?: (email: string, referenceCode: string) => Promise<void>;
  confirmLabel?: string;
  hidePayLater?: boolean;
}

const BillingModal: React.FC<BillingModalProps> = ({
  open,
  campaignId,
  campaignRunLogId,
  campaignName,
  channel,
  onClose,
  totalFee,
  maxBudget,
  estimatedMembers = 0,
  costPerMessage = 0,
  smsSegments = 1,
  campaignStart,
  campaignEnd,
  mode,
  onPaymentLater,
  onSuccess,
  onFailed,
  onConfirmPayment,
  confirmLabel = "Proceed Payment",
  hidePayLater = false,
}) => {
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.sm;

  const [paymentOption, setPaymentOption] = useState<PaymentOption>("account");
  const [selectedAccount, setSelectedAccount] = useState<string | null>(null);
  const [processing, setProcessing] = useState(false);
  const [paymentLaterLoading, setPaymentLaterLoading] = useState(false);
  const [billingAccounts, setBillingAccounts] = useState<BillingAccount[]>([]);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [rescheduleDate, setRescheduleDate] = useState<dayjs.Dayjs | null>(
    null,
  );
  const now = new Date();
  const startOfToday = dayjs().startOf("day");
  const endOfNextMonth = dayjs().add(3, "month").endOf("month");
  const startDate = campaignStart ? new Date(campaignStart) : null;
  const { brandId, campaignInfo } = useBrandStore();
  const [listBlockDate, setListBlockDate] = useState<DateRange[]>([]);
  const isPastCampaign =
    startDate !== null && !isNaN(startDate.getTime()) && startDate < now;

  useEffect(() => {
    if (!open) {
      setPaymentOption("account");
      setSelectedAccount(null);
      setRescheduleDate(null);
    } else {
      fetchBlockDate();
      fetchBillingAccounts();
    }
  }, [open]);

  const fetchBillingAccounts = async () => {
    setLoadingAccounts(true);
    try {
      const data = await billingService.getCreditCard();
      if (data) {
        const accounts: BillingAccount[] = data.map((item) => ({
          email: item.email,
          name: item.email.split("@")[0],
          hasCreditCard: item.isCreditCardAvailable,
        }));
        setBillingAccounts(accounts);

        // if (accounts.length > 0 && !selectedAccount) {
        //   const withCard = accounts.find((a) => a.hasCreditCard);
        //   if (withCard) {
        //     setSelectedAccount(withCard.email);
        //   } else {
        //     setSelectedAccount(accounts[0].email);
        //   }
        // }
      }
    } catch (error) {
      console.error("Failed to fetch billing accounts:", error);
    } finally {
      setLoadingAccounts(false);
    }
  };

  const selectedBillingAccount =
    billingAccounts.find((a) => a.email === selectedAccount) ?? null;

  const estimatedBudget =
    totalFee !== undefined
      ? totalFee
      : channel === ChannelEnum.Sms
        ? estimatedMembers * costPerMessage * smsSegments
        : estimatedMembers * costPerMessage;

  const displayCost =
    mode === ModeEnum.Refresh
      ? (maxBudget ?? estimatedBudget)
      : maxBudget !== undefined && maxBudget < estimatedBudget
        ? maxBudget
        : estimatedBudget;

  const handlePaymentLater = async () => {
    setPaymentLaterLoading(true);
    try {
      await onPaymentLater();
    } finally {
      setPaymentLaterLoading(false);
    }
  };

  const handleConfirmPayment = async () => {
    if (!selectedAccount) return;
    setProcessing(true);
    try {
      const currency = campaignInfo?.Currency || "SGD";
      const payment = await billingService.payTargetMarketing(
        currency,
        displayCost,
        brandId ?? "",
        campaignInfo?.BrandName ?? "",
      );
      if (!payment || payment.data.status !== 1) {
        onFailed();
        return;
      }
      const referenceCode: string = payment.data?.result?.reference_code ?? "";
      if (onConfirmPayment) {
        await onConfirmPayment(selectedAccount, referenceCode);
      } else {
        if (campaignRunLogId) {
          await campaignService.updateCampaignBilled(
            campaignRunLogId,
            referenceCode,
          );
        }
      }
      if (isPastCampaign && rescheduleDate && campaignId) {
        const intervalMinutes = dayjs(campaignEnd).diff(
          dayjs(campaignStart),
          "minute",
        );
        const newStart = rescheduleDate.format("YYYY-MM-DD HH:mm:ss");
        const newEnd = rescheduleDate
          .add(intervalMinutes, "minute")
          .format("YYYY-MM-DD HH:mm:ss");
        await campaignService.rescheduleCampaign(campaignId, newStart, newEnd);
      }
      onSuccess();
    } catch {
      onFailed();
    } finally {
      setProcessing(false);
    }
  };

  const handleConfirm = () => {
    if (paymentOption === "later") {
      handlePaymentLater();
    } else if (paymentOption === "account") {
      handleConfirmPayment();
    }
  };

  const busy = processing || paymentLaterLoading;

  const isConfirmDisabled =
    !paymentOption ||
    (paymentOption === "account" &&
      (!selectedAccount || !selectedBillingAccount?.hasCreditCard)) ||
    (isPastCampaign && paymentOption === "account" && !rescheduleDate);

  const renderRadioDot = (active: boolean) => (
    <div
      style={{
        width: 16,
        height: 16,
        borderRadius: "50%",
        border: `2px solid ${active ? "#1677ff" : "#bfbfbf"}`,
        background: active ? "#1677ff" : "transparent",
        flexShrink: 0,
        marginTop: 3,
      }}
    />
  );

  const fetchBlockDate = async () => {
    if (brandId && channel) {
      try {
        const data = await campaignService.getBlockedCampaignDateRanges(
          brandId,
          channel,
        );
        if (data) {
          setListBlockDate(data);
        }
      } catch (error) {
        console.error("Error fetching postal district:", error);
      }
    }
  };

  const blocked: BlockRange[] = parseBlockedRanges(
    listBlockDate,
    "YYYY-MM-DD HH:mm",
  );

  return (
    <Modal
      open={open}
      onCancel={() => !busy && onClose()}
      footer={null}
      style={{ top: 0 }}
      width={800}
      closable={!busy}
      maskClosable={false}
      styles={{ body: { paddingTop: 12, paddingBottom: 12 } }}
      title={
        <Space>
          {isPastCampaign
            ? "Campaign Reschedule & Payment"
            : "Campaign Preview & Payment"}
        </Space>
      }
    >
      {isPastCampaign && (
        <Alert
          type="error"
          showIcon
          icon={<ExclamationCircleFilled />}
          description={
            <span>This campaign has expired. You can reschedule it.</span>
          }
          style={{ marginBottom: 10 }}
        />
      )}
      <Text>Summary</Text>
      <Card
        style={{
          width: "100%",
          borderRadius: 8,
          marginTop: 10,
          marginBottom: 10,
        }}
        title={
          <Row justify="space-between" align="middle">
            <Col>
              <Text strong>{campaignName}</Text>
            </Col>
            <Col>
              <Tag>One-Time Campaign</Tag>
            </Col>
          </Row>
        }
        size="small"
      >
        {isMobile ? (
          <Row gutter={[0, 10]}>
            {[
              {
                color: "#1677ff",
                label: "Total Members",
                value: `${estimatedMembers.toLocaleString()} Members`,
              },
              {
                color: "#fa541c",
                label: "Max Budget",
                value: `$${displayCost.toFixed(2)}`,
              },
              {
                color: "#722ed1",
                label: "Cost per Message",
                value: `$${costPerMessage} (${channel ? ChannelKeyMap[channel] : "—"})`,
              },
            ].map(({ color, label, value }) => (
              <Col span={24} key={label}>
                <Row justify="space-between" align="middle">
                  <Col>
                    <Text style={{ fontSize: 12 }}>
                      <span style={{ color, marginRight: 6, fontSize: 16 }}>
                        ●
                      </span>
                      {label}
                    </Text>
                  </Col>
                  <Col>
                    <span style={{ fontWeight: 600, color, fontSize: 13 }}>
                      {value}
                    </span>
                  </Col>
                </Row>
              </Col>
            ))}
            <Col span={24}>
              <Row justify="space-between" align="middle">
                <Col>
                  <Text style={{ fontSize: 12 }}>
                    <span
                      style={{
                        color: isPastCampaign ? "#ff4d4f" : "#52c41a",
                        marginRight: 6,
                        fontSize: 16,
                      }}
                    >
                      ●
                    </span>
                    Schedule Send Time
                  </Text>
                </Col>
                <Col>
                  <span
                    style={{
                      fontWeight: 600,
                      color: isPastCampaign ? "#ff4d4f" : "#52c41a",
                      fontSize: 13,
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                    }}
                  >
                    {campaignStart
                      ? dayjs(campaignStart).format("DD/MM/YYYY HH:mm")
                      : "—"}
                    {isPastCampaign && (
                      <Tag color="red" style={{ margin: 0 }}>
                        Expired
                      </Tag>
                    )}
                  </span>
                </Col>
              </Row>
            </Col>
          </Row>
        ) : (
          <Row gutter={[16, 0]}>
            {[
              {
                color: "#1677ff",
                label: "Total Members",
                value: `${estimatedMembers.toLocaleString()} Members`,
              },
              {
                color: "#fa541c",
                label: "Max Budgets",
                value: `$${displayCost.toFixed(2)}`,
              },
              {
                color: "#722ed1",
                label: "Cost per Message",
                value: `$${costPerMessage} (${channel ? ChannelKeyMap[channel] : "—"})`,
              },
            ].map(({ color, label, value }) => (
              <Col span={6} key={label}>
                <div>
                  <Text style={{ fontSize: 12 }}>
                    <span style={{ color, marginRight: 4, fontSize: 16 }}>
                      ●
                    </span>
                    {label}
                  </Text>
                </div>
                <div
                  style={{ fontWeight: 700, color, fontSize: 13, marginTop: 2 }}
                >
                  {value}
                </div>
              </Col>
            ))}
            <Col span={6}>
              <div>
                <Text style={{ fontSize: 12 }}>
                  <span
                    style={{
                      color: isPastCampaign ? "#ff4d4f" : "#52c41a",
                      marginRight: 4,
                      fontSize: 16,
                    }}
                  >
                    ●
                  </span>
                  Scheduled Send Time
                </Text>
              </div>
              <div
                style={{
                  fontWeight: 700,
                  color: isPastCampaign ? "#ff4d4f" : "#52c41a",
                  fontSize: 13,
                  marginTop: 2,
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                }}
              >
                {campaignStart
                  ? dayjs(campaignStart).format("DD/MM/YYYY HH:mm")
                  : "—"}
                {isPastCampaign && (
                  <Tag color="red" style={{ margin: 0 }}>
                    Expired
                  </Tag>
                )}
              </div>
            </Col>
          </Row>
        )}
      </Card>
      {isPastCampaign && (
        <div style={{ marginBottom: 10 }}>
          <Text strong>Reschedule Campaign</Text>
          <Divider style={{ margin: "16px 0 8px" }} />

          <div style={{ marginTop: 8 }}>
            <Text type="secondary" style={{ fontSize: 12, display: "block" }}>
              New Send Date & Time
            </Text>

            <DatePicker
              showTime={{
                format: "HH:mm",
                minuteStep: 15,
                showSecond: false,
              }}
              disabledDate={(current) => {
                if (!current) return false;

                return (
                  current.isBefore(startOfToday, "day") ||
                  current.isAfter(endOfNextMonth, "day")
                );
              }}
              disabledTime={(date) => {
                if (!date) return {};
                const base = buildDisabledTime(date, blocked);
                return applyPastTimeToday(date, base);
              }}
              format="YYYY-MM-DD HH:mm"
              style={{ width: "100%", marginTop: 4 }}
              placeholder="Select Send Date & Time"
              value={rescheduleDate}
              onChange={(date) => setRescheduleDate(date)}
            />
          </div>
        </div>
      )}
      <Text strong>Payment Method</Text>

      <Divider style={{ margin: "16px 0 8px" }} />
      {mode === ModeEnum.Refresh && (
        <Alert
          type="warning"
          showIcon
          description={
            "Final charges may vary based on actual recipients. Unused budget will be refunded"
          }
          style={{ marginTop: 12 }}
        />
      )}
      <Space
        direction="vertical"
        style={{ width: "100%", marginTop: 10 }}
        size={8}
      >
        <div
          style={{
            border: `1.5px solid ${paymentOption === "account" ? "#1677ff" : "#d9d9d9"}`,
            borderRadius: 8,
            background: paymentOption === "account" ? "#e6f4ff" : "#fafafa",
            overflow: "hidden",
            transition: "all 0.2s",
          }}
        >
          <div
            onClick={() => setPaymentOption("account")}
            style={{ padding: "12px 14px", cursor: "pointer" }}
          >
            <Space align="start">
              {!hidePayLater && renderRadioDot(paymentOption === "account")}
              <div>
                <Text strong>GetzPay Account</Text>
                <div>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Select a payment method from your available GetzPay accounts
                  </Text>
                </div>
              </div>
            </Space>
          </div>

          {paymentOption === "account" && (
            <div
              style={{
                borderTop: "1px solid #d9d9d9",
                padding: "10px 14px 14px",
                background: "#fff",
              }}
            >
              <Select
                style={{ width: "100%" }}
                placeholder="Select payment account"
                value={selectedAccount}
                onChange={(val) => setSelectedAccount(val)}
                optionLabelProp="label"
                loading={loadingAccounts}
              >
                {billingAccounts.map((account) => (
                  <Select.Option
                    key={account.email}
                    value={account.email}
                    label={
                      <Space>
                        <span>{account.email}</span>
                      </Space>
                    }
                  >
                    <Row align="middle" justify="space-between" wrap={false}>
                      <Col flex="auto">
                        <div style={{ fontWeight: 600 }}>{account.email}</div>
                      </Col>
                    </Row>
                  </Select.Option>
                ))}
              </Select>

              {selectedBillingAccount &&
                !selectedBillingAccount.hasCreditCard && (
                  <Alert
                    type="error"
                    showIcon
                    icon={<ExclamationCircleFilled />}
                    description={
                      <span>
                        No payment method found for this account. Please{" "}
                        <a
                          href={`${process.env.REACT_APP_DASHBOARD_URL}/billing/credit-card`}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            color: "#1677ff",
                            fontWeight: 700,
                            textDecoration: "underline",
                          }}
                        >
                          add a card
                        </a>{" "}
                        to continue.
                      </span>
                    }
                    style={{ marginTop: 10 }}
                  />
                )}
            </div>
          )}
        </div>

        {!hidePayLater && (
          <div
            onClick={() => setPaymentOption("later")}
            style={{
              padding: "12px 14px",
              border: `1.5px solid ${paymentOption === "later" ? "#1677ff" : "#d9d9d9"}`,
              borderRadius: 8,
              cursor: "pointer",
              background: paymentOption === "later" ? "#e6f4ff" : "#fafafa",
              transition: "all 0.2s",
            }}
          >
            <Space align="start">
              {renderRadioDot(paymentOption === "later")}
              <div>
                <Text strong>Setup Payment Later</Text>
                <div>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Payment must be completed before the scheduled send time.
                  </Text>
                </div>
              </div>
            </Space>
          </div>
        )}
      </Space>

      <Row gutter={12} style={{ marginTop: 20 }}>
        <Col span={12}>
          <Button
            size="large"
            className="w-100"
            disabled={busy}
            onClick={onClose}
          >
            Go Back
          </Button>
        </Col>
        <Col span={12}>
          <Button
            type="primary"
            size="large"
            className="w-100"
            disabled={isConfirmDisabled || busy}
            loading={processing || paymentLaterLoading}
            onClick={handleConfirm}
          >
            {confirmLabel}
          </Button>
        </Col>
      </Row>
    </Modal>
  );
};

export default BillingModal;
